/* =========================================================================
   CheckoutSuccess — /checkout/success

   Stripe redirects here after a successful Checkout. Entitlement, however, is
   granted asynchronously by the Stripe webhook (it writes courseAccess /
   subscriptionStatus to Firestore), so the redirect usually lands BEFORE the
   client knows the user has paid. Dropping them straight onto a paywalled
   route would bounce them back to /subscriptions.

   So this screen polls refreshUser() (re-reads the Firestore user doc) until
   the entitlement shows up, then offers a single CTA back to wherever the
   paywall originally interrupted them (?next=...), defaulting to the course.

   DESKTOP CREDITS ARE THE SECOND KIND OF PURCHASE, AND THEY VERIFY DIFFERENTLY
   A credit pack grants no entitlement — it moves a number in a collection the
   browser cannot read — so refreshUser() would never see it land. Worse, the
   entitlement test below is TRUE for any client who already owns the course,
   so reusing it would flash "you're in" for a top-up that had not landed, or
   had failed outright. The credits path instead polls the backend for the one
   Stripe session it is actually waiting on (?session_id=), which answers about
   that payment and nothing else.
   ========================================================================= */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserAuth } from "../../context/UserContext";
import { requestCreditGrantStatus } from "../../services/userService";
import { getFreshIdToken } from "../../utils/authToken.js";
import useReveal from "hooks/useReveal";
import "./CheckoutSuccess.css";

// Where an entitled user lands if no explicit destination was preserved.
const DEFAULT_NEXT = "/claudedev-table";
// Credits are bought from the account page and read back there, so that is
// where a top-up returns to.
const DEFAULT_CREDITS_NEXT = "/accountsettings";

// Poll cadence: ~30s of attempts is enough to cover normal webhook latency
// without leaving the user staring at a spinner forever.
const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 12;

// Credits are whole cents of Codenec-charged cost — shown as money, the same
// way the account page and the desktop app's own rail show them.
const formatCredits = (credits) => `$${((Number(credits) || 0) / 100).toFixed(2)}`;

// A one-time course purchase OR an active/trialing subscription unlocks access
// (mirrors the check in RestrictedRoute).
const isEntitled = (u) =>
  !!u &&
  (u.courseAccess === true ||
    (u.hasPaid &&
      (u.subscriptionStatus === "active" || u.subscriptionStatus === "trialing")));

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, refreshUser } = useUserAuth();
  const [searchParams] = useSearchParams();

  // The card rises in once on load (styles/premium.css [data-reveal]).
  useReveal();

  // Which purchase this screen is confirming. `product` is written into the
  // success URL by whichever checkout started it.
  const isCredits = searchParams.get("product") === "desktop_credits";
  // Stripe substitutes the real id into {CHECKOUT_SESSION_ID} on the redirect.
  const sessionId = searchParams.get("session_id") || "";

  // Only honor same-site absolute paths as the return destination.
  const rawNext = searchParams.get("next") || "";
  const fallbackNext = isCredits ? DEFAULT_CREDITS_NEXT : DEFAULT_NEXT;
  const nextSafe =
    rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : fallbackNext;

  const [status, setStatus] = useState(() =>
    // NEVER the entitlement shortcut for credits: it is true for anyone who
    // already owns the course, which would greet a failed top-up with "you're
    // in". A credit pack is only ever confirmed by its own grant.
    !isCredits && isEntitled(user) ? "success" : "verifying"
  );
  const [rechecking, setRechecking] = useState(false);
  // Said through the status region when "Check again" finds nothing yet.
  const [recheckNote, setRecheckNote] = useState("");
  // What actually landed: the pack's credits and the balance after it. Null
  // until the grant is seen, so the success copy can never quote a number that
  // has not been confirmed by the server.
  const [grant, setGrant] = useState(null);

  // When the state changes (not on first load), focus moves to the new
  // state's title, so keyboard and screen-reader users land on it.
  const titleRef = useRef(null);
  const shownStatus = useRef(status);
  useEffect(() => {
    if (shownStatus.current === status) return;
    shownStatus.current = status;
    if (status !== "verifying") titleRef.current?.focus();
  }, [status]);

  // One attempt at "has this purchase landed yet?", answering true when it
  // has. The two products verify against completely different things, so the
  // difference lives here and the polling loop below stays shared.
  const checkOnce = async () => {
    if (!isCredits) return isEntitled(await refreshUser());

    if (!sessionId) return false; // nothing to ask about; falls through to timeout
    try {
      const token = await getFreshIdToken(user?.accessToken);
      const result = await requestCreditGrantStatus(token, sessionId);
      if (!result?.granted) return false;
      setGrant({ credits: result.credits, balance: result.balance });
      return true;
    } catch (error) {
      // A blip mid-poll is not a failed purchase. Keep waiting; the timeout
      // state is what eventually says "check your account page".
      console.error("Credit grant check failed:", error?.response?.status || error?.message);
      return false;
    }
  };

  useEffect(() => {
    // No signed-in user (e.g. session lost) — can't verify a purchase.
    if (!user) {
      setStatus("signedOut");
      return;
    }
    // Already entitled (returning subscriber, or a very fast webhook). Course
    // and subscription only — see the note on the initial state above.
    if (!isCredits && isEntitled(user)) {
      setStatus("success");
      return;
    }

    let cancelled = false;
    let timerId;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      const landed = await checkOnce();
      if (cancelled) return;

      if (landed) {
        setStatus("success");
        return;
      }
      if (attempts >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      timerId = setTimeout(poll, POLL_INTERVAL_MS);
    };

    timerId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
    // Poll once per mount; refreshUser/user are stable enough for this flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual re-check from the timeout state (webhook may have just landed).
  const handleRecheck = async () => {
    if (rechecking) return;
    setRechecking(true);
    setRecheckNote("");
    const landed = await checkOnce();
    setRechecking(false);
    if (landed) setStatus("success");
    else setRecheckNote(t("checkoutSuccess.stillNotConfirmed"));
  };

  const goNext = () => navigate(nextSafe);
  const goSignIn = () => navigate("/login-page");

  /**
   * The same six pieces of copy, in whichever voice this purchase needs.
   * "Your access is now active" is wrong for a top-up, and "credits added" is
   * wrong for a course, so the credits strings live in their own block under
   * the same names rather than being bent to cover both.
   */
  const copy = (key, vars) =>
    isCredits ? t(`checkoutSuccess.credits.${key}`, vars) : t(`checkoutSuccess.${key}`, vars);

  return (
    <main className="cs-page" id="main" tabIndex={-1}>
      <span className="cn-grain" aria-hidden="true" />
      <div className="cs-card" data-reveal="">
        {/* The spinner's text, and what a failed "Check again" found. Other
            state changes are read when focus moves to the new title. */}
        <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {status === "verifying" ? copy("verifyingTitle") : recheckNote}
        </p>

        {/* Each state sits in its own .cs-state wrapper, so a status change
            mounts a fresh one and it fades in instead of snapping. */}
        {status === "verifying" && (
          <div className="cs-state">
            <div className="cs-icon" aria-hidden="true">
              <div className="cs-spinner" />
            </div>
            <h1 className="cs-title">{copy("verifyingTitle")}</h1>
            <p className="cs-body">{copy("verifyingBody")}</p>
          </div>
        )}

        {status === "success" && (
          <div className="cs-state">
            <div className="cs-icon">
              <div className="cs-check">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 12.5l5 5 11-11"
                    stroke="#06121f"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <h1 className="cs-title" ref={titleRef} tabIndex={-1}>{copy("successTitle")}</h1>
            <p className="cs-body">
              {/* The numbers come from the grant the server confirmed, never
                  from the URL or from a guess: this line is the receipt. */}
              {copy("successBody", {
                credits: formatCredits(grant?.credits),
                balance: formatCredits(grant?.balance),
              })}
            </p>
            <button className="cs-btn cs-btn-primary" onClick={goNext}>
              {copy("continueCta")}
            </button>
          </div>
        )}

        {status === "timeout" && (
          <div className="cs-state">
            <div className="cs-icon">
              {/* Line icons rather than emoji, so the glyph matches the
                  check and spinner on every platform. */}
              <div className="cs-clock" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8.5" />
                  <path d="M12 7.5V12l3 2" />
                </svg>
              </div>
            </div>
            <h1 className="cs-title" ref={titleRef} tabIndex={-1}>{copy("timeoutTitle")}</h1>
            <p className="cs-body">{copy("timeoutBody")}</p>
            {/* aria-disabled, not disabled: a disabled button would drop
                keyboard focus while the check runs. */}
            <button
              className="cs-btn cs-btn-primary"
              onClick={handleRecheck}
              aria-disabled={rechecking}
            >
              {copy("checkAgainCta")}
            </button>
            <p className="cs-support">{copy("supportNote")}</p>
          </div>
        )}

        {status === "signedOut" && (
          <div className="cs-state">
            <div className="cs-icon">
              <div className="cs-clock" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="5" y="10.5" width="14" height="9.5" rx="2.5" />
                  <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
                </svg>
              </div>
            </div>
            <h1 className="cs-title" ref={titleRef} tabIndex={-1}>{copy("signedOutTitle")}</h1>
            <p className="cs-body">{copy("signedOutBody")}</p>
            <button className="cs-btn cs-btn-primary" onClick={goSignIn}>
              {copy("signInCta")}
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default CheckoutSuccess;
